<script setup lang="ts">
import { UiButton, UiModal } from "@packages/component-library";
import { computed, onMounted, ref } from "vue";
import { useWindowSize } from "@vueuse/core";
import { userLocationStore } from "@/modules/user-location";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["change", "close"]);

const { t } = useI18n();
const { width } = useWindowSize();

const locationStore = userLocationStore();
const systemCity = computed(() => locationStore.city);
const showModal = ref(false);

const saveUserCity = () => {
  localStorage.setItem("userCity", locationStore.city.id);
  emit("close");
};

onMounted(() => {
  showModal.value = true;
});
</script>

<template>
  <div v-if="width > 1239" class="city-autocomplete">
    <h5>{{ t("header.userCity") }}</h5>
    <p>{{ systemCity?.title }}</p>
    <div class="city-autocomplete__actions">
      <UiButton
        size="m"
        :label="t('system.change')"
        color="secondary"
        data-test-id="header-button-changeCity"
        @click="emit('change')"
      />
      <UiButton
        size="m"
        :label="t('system.confirm')"
        data-test-id="header-button-confirmCity"
        @click="saveUserCity"
      />
    </div>
  </div>

  <UiModal
    v-else
    v-model="showModal"
    hide-header
    hide-close
    size="s"
    @update:modelValue="emit('close')"
  >
    <template #default>
      <div class="city-autocomplete">
        <h5>{{ t("header.userCity") }}</h5>
        <p>{{ systemCity.title }}</p>
      </div>
    </template>
    <template #footer>
      <div class="city-autocomplete__actions">
        <UiButton
          size="m"
          :label="t('system.change')"
          color="secondary"
          data-test-id="cityAutocomplete-button-changeCity"
          @click="emit('change')"
        />
        <UiButton
          size="m"
          :label="t('system.confirm')"
          data-test-id="cityAutocomplete-button-confirmCity"
          @click="saveUserCity"
        />
      </div>
    </template>
  </UiModal>
</template>

<style scoped lang="scss">
.city-autocomplete {
  position: absolute;
  padding: calc($base-space * 8);
  border-radius: 12px;
  background-color: var(--color-semantic-background-normal-primary);
  box-shadow: 0 0 16px 0 rgba(33, 34, 36, 0.08);
  top: 48px;
  width: 400px;

  @media (max-width: 1239px) {
    width: auto;
    position: static;
    padding: 0;
    border-radius: 0;
    background: none;
    box-shadow: none;
    top: 0;
  }

  > h5 {
    @include body-m();
    color: var(--color-semantic-content-normal-primary);
  }

  > p {
    margin: calc($base-space * 2) 0 0;
    @include h4();
    color: var(--color-semantic-content-normal-primary);
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: calc($base-space * 2);
    width: 100%;

    margin: calc($base-space * 6) 0 0;

    @media (max-width: 1023px) {
      margin: 0;
      flex-flow: column-reverse;
    }

    > button {
      @media (max-width: 1023px) {
        width: 100%;
      }
    }
  }
}
</style>
